import { Injectable } from '@angular/core';
import { LMToolbarServiceBase } from '@toolbar/lm-toolbar-service.base';
import { LMToolbarServiceAdapter } from '@toolbar/lm-toolbar-service-adapter';
import { AppConstants } from '@app/utils';
import { LMReport } from '@report/lm-reports';
import { LMConfigurationService, LMContextService } from '@app/services';

@Injectable()
export class LMReportsCatalogToolbarService extends LMToolbarServiceBase {
  fakePageState: PageStateInfo = {
    isModified: false,
    selectedPanel: undefined
  };
  actionRefArray: Array<LMBusActionReference> | undefined;

  constructor(
    public lmToolbarServiceAdapter: LMToolbarServiceAdapter,
    public lmConfigurationService: LMConfigurationService,
    public lmContextService: LMContextService,
    ) {
    super(lmToolbarServiceAdapter); // must msg the base class constructor
  }

  public buildToolbar(actionRefArray: Array<LMBusActionReference>): void {
    this.actionRefArray = actionRefArray;
    this.updateToolbar();
  }

  public updateToolbar() {
    if (this.actionRefArray) {
      this.toolbarButtons = this.reportsActionRefArrayToToolbarButtonArray(this.actionRefArray);
      this._updateToolbarState(this.toolbarButtons, this.fakePageState);
      this.fireToolbarObservable();
    }
  }

  private reportsActionRefArrayToToolbarButtonArray(actionRefArray: LMBusActionReference[]): LMToolbarButton[] {
    return actionRefArray.map(actionRef =>
    // @ts-ignore
      this.buildLMToolbarButton(null, actionRef, true, false, false));
  }

  private _updateToolbarState(toolbarButtons: LMToolbarButton[], pageStateInfo: PageStateInfo) {
    toolbarButtons.forEach((toolbarButton: LMToolbarButton) => {
      if (toolbarButton.actionReference?.id === AppConstants.STANDARD_SAVE && toolbarButton.state) {
        toolbarButton.state.disabled = !pageStateInfo.isModified;
      }
      // Disables PDF button for unsupported types
      if (toolbarButton.actionReference?.id === AppConstants.STANDARD_TOOLBAR_EXPORTPDF && toolbarButton.state) {
        toolbarButton.state.disabled = true;
      }
    });
  }

  public buildReportContextMenu(report: any, response: SohoPopupMenuResponseFunction): void {
    const type = report.type.toUpperCase();
    const isOwner = (report.owner === this.lmContextService.getActorId());
    let markup = '';
    /*   User-Created Lists    | Delivered Lists    | All Other Reports
     *   ======================|====================|======================
     *    Copy                 |  Copy              |  No Available Actions
     *    Delete               |  Export to CSV     |
     *    Settings             |  Settings          |
     *    Reassign             |  Print to PDF      |
     *    Export to CSV        |  Saved Searches    |
     *    Print to PDF         |
     *    Saved Search         |
     */
    if (report && type === 'LIST') {
      if (this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_CreateReport)) {
        markup += this.buildContextMenuLi(report.id, 'copy', Soho.Locale.translate('Copy'));
      }
      if (isOwner) {
        markup += this.buildContextMenuLi(report.id, 'delete', Soho.Locale.translate('Delete'));
        markup += this.buildContextMenuLi(report.id, 'runPersonalization', Soho.Locale.translate('Settings'));

        if (report.validActions?.includes('ReassignReportOwner')) {
          markup += this.buildContextMenuLi(report.id, 'reassignReportOwner', Soho.Locale.translate('ReassignReportOwner'));
        }
      } else { // Not Owner
        markup += this.buildContextMenuLi(report.id, 'viewPersonalization', Soho.Locale.translate('Settings'));
      }
      if (LandmarkContext.configuredFeatures?.includes('ExportToCSV')) {
        markup += this.buildContextMenuLi(report.id, 'exportToCsv', Soho.Locale.translate('ExportToCSV'));
      }
      if (LandmarkContext.configuredFeatures?.includes('ExportToPDF')) {
        markup += this.buildContextMenuLi(report.id, 'printToPdf', Soho.Locale.translate('PrintToFile'));
      }
      if (this.lmContextService.showUserListView()) {
        markup += this.buildSubmenuLi(report, 'SavedSearch', Soho.Locale.translate('SavedSearches'));
      }
    }
    if (markup === '') { // Delivered Pages & External (eg: EPM, WFM)
      markup += this.buildContextMenuLi('', '', Soho.Locale.translate('NoActionsAvailable'));
    }
    response(markup);
  }

  private buildContextMenuLi(reportId: string, actionName: string, displayName: string) {
    let li = '';
    if (actionName !== '') {
      li =
        `<li id="${this.menuIdV1(reportId)}">
          <a id="${this.menuIdV2(reportId)}"
            data-automation-id="lm-${reportId}-toolbar-menuitem-${actionName}"
            data-action-name="${actionName}"
            >${displayName}</a>
        </li>`;
    } else { // If no action, disable the item
      li =
        `<li id="${this.menuIdV1(reportId)}" class="is-disabled">
          <a id="${this.menuIdV2(reportId)}"
            data-automation-id="lm-${reportId}-toolbar-menuitem-${actionName}"
            data-action-name="${actionName}"
            >${displayName}</a>
        </li>`;
    }
    return li;
  }

  private buildSubmenuLi(report: LMReport, id: string, label: string): string {
    // let savedSearchMenuItems = this.getStandardSeparatorMenuItem();
    const li = `<li id="" class="submenu is-focused" role="none">
        <a id="Catalog${id}MenuButton_menuitem"
        data-automation-id="lm-${report.id}-CatalogSavedSearchMenuButton-toolbar-menuitem"
        attr.data-action-reference="{}"
        data-action-reference="{}"
        role="menuitem"
        tabindex="-1"
        aria-haspopup="true"><span>${label}</span><svg class="arrow icon-dropdown icon" focusable="false" aria-hidden="true" role="presentation"><use href="#icon-dropdown"></use></svg></a>
        <ul class="popupmenu" id="Catalog${id}MenuButton"></ul>
      </li>`;
    return li;
  }

  public buildSavedSearchContextMenu(report: LMReport, response: any, isShowMore?: boolean) {
    let userListViewUrl = report.url.substring(report.url.indexOf('/' + LandmarkContext.urlPdlOrDa + '/'));
    userListViewUrl = userListViewUrl.replace('<WebAppName>', LandmarkContext.webApp);
    userListViewUrl = userListViewUrl.replace('/list/', '/config/');
    const userFolderId = (report.id === report.busClass + '.' + report.name) ? '' : report.id;
    const lk = isShowMore ? 'UserListView[ByViewListActor](' + report.busClass + ',' + report.name + ',' + this.lmContextService.getActorId() + ',' + userFolderId + ',5)' : undefined ;
    this.lmConfigurationService.fetchUserListViews(userListViewUrl, lk).subscribe((result: LMUserListViewResponse) => {
      let menu = '';
      if (result.savedViews?.length > 0) {
        for (const savedView of result.savedViews) {
          menu += this.buildSavedSearchMenuLi(report, savedView);
        }
        if (result.hasMore) {
          menu += this.buildSubmenuLi(report, 'More', Soho.Locale.translate('More'));
        }
      } else {
        menu += this.buildContextMenuLi('', '', Soho.Locale.translate('NoSavedSearches'));
      }
      response(menu);
    });
  }

  private buildSavedSearchMenuLi(report: LMReport, savedSearch: LMUserListViewItem) {
    const li = `<li id="${this.menuIdV1(report.id)}">
          <a id="${this.menuIdV2(report.id)}"
            data-automation-id="lm-${report.id}-savedsearch-menuitem"
            data-action-name="open"
            data-list-url="${report.url}"
            data-user-list-view="${savedSearch.userListView}"
            >${savedSearch.name}</a>
        </li>`;
    return li;
  }
}